// Phone Today (view 'home') — window.PhoneToday.
//
// Not the desktop Home reflowed: a phone's first screen answers "what's
// next and what's due". Four independent sections, fetched in parallel, each
// with its own skeleton / empty / error state so one slow call never holds
// the rest:
//   • Next up        — today's remaining meetings (/api/calendar/events, today)
//   • Due            — my open tasks due today or earlier (the same listTasks
//                      call Home and the bell make; apiFetch shares it)
//   • Pinned deals   — /api/engagements/pins
//   • Needs attention — my deals that went quiet (engagements attention_only,
//                      the predicate desktop Home uses), just-crossed first
// Deliberately NOT fetched: /dashboard/counts, the pipeline aggregate and the
// other Home aggregates — 3–6 s each, and they timed out on iPhone.

const PD_TAP = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };
const PD_DUE_SHOWN = 8;
const PD_ATTENTION_SHOWN = 6;
// Mirrors the server's _attention_filter (routes/engagements.py) and desktop
// Home: active deals alert after 90 days without a touch, on-hold after 180.
const PD_ACTIVE_STALE_DAYS = 90;
const PD_ON_HOLD_STALE_DAYS = 180;
const PD_DAY_MS = 86400000;

// Latest touch on a deal = max(stage move, linked-note activity) — as Home.
const pdTouchOf = (d) => {
  const stamps = [d._raw && d._raw.last_stage_update, d.lastActivityAt]
    .filter(Boolean).map((iso) => new Date(iso).getTime()).filter((t) => !Number.isNaN(t));
  return stamps.length ? Math.max(...stamps) : null;
};
// Buyer shells read "Buyer › Seller deal" with their real (DealCloud) stage.
const pdDealLabel = (d) => (d.type === 'buyer_buyside' && d.sellerEngagementName ? `${d.name} › ${d.sellerEngagementName}` : d.name);
const pdDealStage = (d) => (d.type === 'buyer_buyside' && d.dcStage ? d.dcStage.replace(/^\d+\.\s*/, '') : (d.stageName || d.stage));

// Home's urgency ranking: deals that only just crossed their staleness
// threshold first — still warm, worth a nudge today.
const pdRankAttention = (deals) => {
  const now = Date.now();
  return (deals || [])
    .map((d) => { const t = pdTouchOf(d); return { d, age: t != null ? (now - t) / PD_DAY_MS : null }; })
    .filter(({ d, age }) => !d.stale && age != null && age >= (d.onHold ? PD_ON_HOLD_STALE_DAYS : PD_ACTIVE_STALE_DAYS))
    .map((x) => ({ ...x, past: x.age - (x.d.onHold ? PD_ON_HOLD_STALE_DAYS : PD_ACTIVE_STALE_DAYS) }))
    .sort((a, b) => a.past - b.past);
};

const pdGreeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};
// "Now" · "in 25 min" · "at 2:30pm"
const pdStartsIn = (ev, now) => {
  if (ev._start <= now) return 'Now';
  const m = Math.round((ev._start - now) / 60000);
  if (m < 60) return `in ${Math.max(1, m)} min`;
  return `at ${window.AtlasCal.calFmtTime(ev._startD)}`;
};

// Loader state: { data: null } while loading, { data, error } after.
const PD_LOADING = { data: null, error: null };
const pdIs403 = (e) => /^403\b/.test((e && e.message) || '');

const PdGroup = ({ children }) => (
  <div style={{ borderTop: '1px solid var(--border)' }}>{children}</div>
);
const PdNote = ({ children }) => (
  <div style={{ padding: '14px 16px', fontSize: 14.5, color: 'var(--text-3)', background: 'var(--surface)', borderBottom: '1px solid var(--border)', lineHeight: 1.45 }}>{children}</div>
);
const PdError = ({ text, onRetry }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 6px 6px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
    <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, color: 'var(--text-3)' }}>{text}</span>
    <button type="button" onClick={onRetry}
      style={{ ...PD_TAP, height: 44, padding: '0 12px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Retry</button>
  </div>
);
const PdMore = ({ label, onClick }) => (
  <button type="button" onClick={onClick}
    style={{ ...PD_TAP, display: 'flex', alignItems: 'center', width: '100%', minHeight: 48, padding: '0 16px', border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface)', textAlign: 'left', fontSize: 15, color: 'var(--accent-text)', cursor: 'pointer' }}>
    <span style={{ flex: 1 }}>{label}</span>
    <Icon.ChevronR size={14} />
  </button>
);

// The first remaining meeting, as a card: when, what, with whom, and Join.
const PdNextCard = ({ ev, now, onOpen }) => {
  const live = window.PhoneCalKit.isLive(ev, now);
  const who = [ev.company && ev.company.name, ev.contact && ev.contact.name].filter(Boolean).join(' · ')
    || (ev.internal_only ? 'Internal' : 'Not linked to a record');
  return (
    <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <button type="button" onClick={() => onOpen(ev)}
        style={{ ...PD_TAP, display: 'block', width: '100%', padding: '14px 16px 12px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit' }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: live ? 'var(--accent-text)' : 'var(--text)' }}>{pdStartsIn(ev, now)}</span>
          <span className="num" style={{ fontSize: 13.5, color: 'var(--text-3)' }}>{window.PhoneCalKit.timeRange(ev)}</span>
        </span>
        <span style={{ display: 'block', marginTop: 4, fontSize: 18, fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{ev.title}</span>
        <span style={{ display: 'block', marginTop: 3, fontSize: 14, color: !ev.linked && !ev.internal_only ? 'var(--amber-fg)' : 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {who}{(ev.external_attendees || []).length ? ` · ${ev.external_attendees.length} guest${ev.external_attendees.length === 1 ? '' : 's'}` : ''}
        </span>
      </button>
      {ev.join_url && (
        <div style={{ padding: '0 16px 14px' }}>
          <button type="button" onClick={() => window.PhoneCalKit.open(ev.join_url)}
            style={{ ...PD_TAP, width: '100%', height: 46, borderRadius: 12, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Icon.Play size={15} /> Join meeting
          </button>
        </div>
      )}
    </div>
  );
};

const PhoneToday = ({ activeUser, teamMembers, setView, goToDeal, goToCompany, goToContact }) => {
  const K = window.PhoneTaskKit;
  const CK = window.PhoneCalKit;
  const meId = (activeUser && activeUser.id) || null;
  const [meet, setMeet] = React.useState(PD_LOADING);
  const [due, setDue] = React.useState(PD_LOADING);
  const [pins, setPins] = React.useState(PD_LOADING);
  const [attn, setAttn] = React.useState(PD_LOADING);
  const [now, setNow] = React.useState(Date.now());
  const [openEv, setOpenEv] = React.useState(null);
  const [sheetTask, setSheetTask] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [allAttn, setAllAttn] = React.useState(false);
  const nav = { goToDeal, goToCompany, goToContact };

  // Each loader: quiet = keep showing what's there while refetching (event /
  // resume refreshes); a quiet failure keeps the old data.
  const loadMeetings = React.useCallback((quiet) => {
    if (!quiet) setMeet(PD_LOADING);
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 1);
    return window.AtlasApi.calendarEvents({ start: start.toISOString(), end: end.toISOString() })
      .then((res) => { setMeet({ data: window.AtlasCal.normalizeEvents(res), error: null }); setNow(Date.now()); })
      .catch((e) => setMeet((s) => (quiet && s.data ? s : { data: [], error: e.message || 'error' })));
  }, []);

  const loadDue = React.useCallback((quiet) => {
    // Who "you" are isn't known until whoami lands — stay loading rather
    // than claim there's nothing due (the shell flags an unreachable API).
    if (!meId) { setDue(PD_LOADING); return Promise.resolve(); }
    if (!quiet) setDue(PD_LOADING);
    return window.AtlasApi.listTasks({ assigned_to: meId, status: 'open', limit: 100 })
      .then((res) => setDue({ data: ((res && res.items) || []).filter(K.isDueNow), error: null }))
      .catch((e) => setDue((s) => (quiet && s.data ? s : { data: [], error: e.message || 'error' })));
  }, [meId]);

  const loadPins = React.useCallback((quiet) => {
    if (!quiet) setPins(PD_LOADING);
    return window.AtlasApi.listPinnedDeals()
      .then((res) => {
        const items = Array.isArray(res) ? res : ((res && res.items) || []);
        setPins({ data: items.map(window.AtlasData.__adaptEngagement), error: null });
      })
      // A role without deal access has no pins to show — hide the section.
      .catch((e) => setPins((s) => (pdIs403(e) ? { data: [], error: null, hidden: true } : quiet && s.data ? s : { data: [], error: e.message || 'error' })));
  }, []);

  // The user's own seller-side deals (buyer shells would flood it — a blast
  // touches dozens at once), attention_only + attention_sort=recent like Home.
  const loadAttn = React.useCallback((quiet) => {
    if (!meId) { setAttn(PD_LOADING); return Promise.resolve(); }
    if (!quiet) setAttn(PD_LOADING);
    return window.AtlasApi.listEngagements({
      dealmaker_id: meId, include_archived: false, attention_only: true, attention_sort: 'recent',
      exclude_engagement_type: 'buyer_buyside', limit: 25,
    })
      .then((res) => setAttn({ data: pdRankAttention(((res && res.items) || []).map(window.AtlasData.__adaptEngagement)), error: null }))
      .catch((e) => setAttn((s) => (pdIs403(e) ? { data: [], error: null, hidden: true } : quiet && s.data ? s : { data: [], error: e.message || 'error' })));
  }, [meId]);

  React.useEffect(() => { loadMeetings(false); }, [loadMeetings]);
  React.useEffect(() => { loadDue(false); }, [loadDue]);
  React.useEffect(() => { loadPins(false); }, [loadPins]);
  React.useEffect(() => { loadAttn(false); }, [loadAttn]);

  // Targeted refreshes for the events the rest of the app fires; everything
  // on return to the app or after a broad data change.
  React.useEffect(() => {
    const onTasks = () => loadDue(true);
    const onDeals = () => { loadAttn(true); loadPins(true); };
    const onPins = () => loadPins(true);
    window.addEventListener('task-created', onTasks);
    window.addEventListener('deal-updated', onDeals);
    window.addEventListener('pins-changed', onPins);
    return () => {
      window.removeEventListener('task-created', onTasks);
      window.removeEventListener('deal-updated', onDeals);
      window.removeEventListener('pins-changed', onPins);
    };
  }, [loadDue, loadAttn, loadPins]);
  K.useRefresh(() => { loadMeetings(true); loadDue(true); loadPins(true); loadAttn(true); }, ['atlas-data-changed']);
  // Meetings fall off "Next up" as they end; "in 12 min" counts down.
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);

  // Completing from here lingers 5s with Undo, then the Due list re-pulls
  // (via 'task-created', which also refreshes the Tasks-tab badge).
  const toggle = window.useTaskToggle(K.tasksChanged);

  // Timed meetings that start before midnight and haven't ended (the API
  // returns anything overlapping the window, so guard both edges).
  const midnight = new Date(now); midnight.setHours(24, 0, 0, 0);
  const timedToday = (meet.data || []).filter((e) => !e.is_all_day && e._startD && e._start < midnight.getTime());
  const remaining = timedToday.filter((e) => e._end > now).sort((a, b) => a._start - b._start);
  const doneToday = timedToday.filter((e) => e._end <= now).length;
  const dueList = due.data || [];
  const overdueN = dueList.filter((t) => (K.dayDiff(t.due_date) ?? 0) < 0).length;
  const attnList = attn.data || [];

  const today = new Date(now);
  const summary = [
    meet.data && !meet.error ? (remaining.length ? `${remaining.length} meeting${remaining.length === 1 ? '' : 's'} left` : 'No more meetings') : null,
    due.data && !due.error ? (dueList.length ? `${dueList.length} task${dueList.length === 1 ? '' : 's'} due${overdueN ? ` (${overdueN} overdue)` : ''}` : 'Nothing due') : null,
  ].filter(Boolean).join(' · ');
  const firstName = ((activeUser && (activeUser.first_name || activeUser.full_name)) || '').split(' ')[0];

  return (
    <window.PhoneScroll>
      <div style={{ padding: '16px 16px 4px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {today.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em', marginTop: 2 }}>
          {pdGreeting()}{firstName ? `, ${firstName}` : ''}
        </div>
        <div style={{ fontSize: 14.5, color: 'var(--text-3)', marginTop: 4, minHeight: 20 }}>{summary}</div>
      </div>

      {/* ── Next up ─────────────────────────────────────────────────── */}
      <window.PhoneSectionHeader title="Next up" count={meet.data && !meet.error && remaining.length ? remaining.length : null}
        action={<button type="button" onClick={() => setView('calendar')} style={{ ...PD_TAP, height: 32, margin: '-8px -8px -8px 0', padding: '0 8px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Calendar</button>} />
      <PdGroup>
        {meet.data === null && <SkeletonRows rows={2} />}
        {meet.data && meet.error && <PdError text="Couldn't load your meetings." onRetry={() => loadMeetings(false)} />}
        {meet.data && !meet.error && remaining.length === 0 && (
          <PdNote>{doneToday ? `That's it for meetings today — ${doneToday} done.` : 'No meetings on your calendar today.'}</PdNote>
        )}
        {meet.data && !meet.error && remaining.length > 0 && (
          <>
            <PdNextCard ev={remaining[0]} now={now} onOpen={setOpenEv} />
            {remaining.slice(1).map((ev) => <CK.EventRow key={ev.id} ev={ev} now={now} onOpen={setOpenEv} />)}
          </>
        )}
      </PdGroup>

      {/* ── Due ─────────────────────────────────────────────────────── */}
      <window.PhoneSectionHeader title="Due" count={due.data && !due.error && dueList.length ? dueList.length : null}
        action={<button type="button" onClick={() => setView('inbox')} style={{ ...PD_TAP, height: 32, margin: '-8px -8px -8px 0', padding: '0 8px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>All tasks</button>} />
      <PdGroup>
        {due.data === null && <SkeletonRows rows={3} />}
        {due.data && due.error && <PdError text="Couldn't load your tasks." onRetry={() => loadDue(false)} />}
        {due.data && !due.error && dueList.length === 0 && <PdNote>Nothing due today. Nice.</PdNote>}
        {due.data && !due.error && dueList.slice(0, PD_DUE_SHOWN).map((t) => (
          <K.Row key={t.id} t={t} toggle={toggle} meId={meId}
            // Tap goes where the task is about; a task about nothing opens its sheet.
            onTap={(x) => { if (!K.openLinked(x, nav)) setSheetTask(x); }}
            onMore={setSheetTask} />
        ))}
        {due.data && !due.error && dueList.length > PD_DUE_SHOWN && (
          <PdMore label={`See all ${dueList.length} in Tasks`} onClick={() => setView('inbox')} />
        )}
      </PdGroup>

      {/* ── Pinned deals ────────────────────────────────────────────── */}
      {!pins.hidden && (
        <>
          <window.PhoneSectionHeader title="Pinned deals" count={pins.data && !pins.error && pins.data.length ? pins.data.length : null} />
          <PdGroup>
            {pins.data === null && <SkeletonRows rows={2} variant="engagement" />}
            {pins.data && pins.error && <PdError text="Couldn't load your pinned deals." onRetry={() => loadPins(false)} />}
            {pins.data && !pins.error && pins.data.length === 0 && (
              <PdNote>Pin a deal from its page (the pin next to its name).</PdNote>
            )}
            {pins.data && !pins.error && pins.data.map((d) => (
              <window.PhoneRow key={d.id} onClick={() => goToDeal(d.id)}
                leading={<Logo name={d.targetName || d.name} size={32} color={window.hashColor(d.targetName || d.name)} />}
                title={pdDealLabel(d)}
                subtitle={[pdDealStage(d), d.onHold ? 'On hold' : null, d.lastActivityAt ? `last touch ${window.relativeTime(d.lastActivityAt)}` : null].filter(Boolean).join(' · ')} />
            ))}
          </PdGroup>
        </>
      )}

      {/* ── Needs attention ─────────────────────────────────────────── */}
      {!attn.hidden && (
        <>
          <window.PhoneSectionHeader title="Needs attention" count={attn.data && !attn.error && attnList.length ? attnList.length : null} />
          <PdGroup>
            {attn.data === null && <SkeletonRows rows={3} variant="engagement" />}
            {attn.data && attn.error && <PdError text="Couldn't load your deals." onRetry={() => loadAttn(false)} />}
            {attn.data && !attn.error && attnList.length === 0 && (
              <PdNote>None of your deals have gone quiet.</PdNote>
            )}
            {attn.data && !attn.error && attnList.slice(0, allAttn ? attnList.length : PD_ATTENTION_SHOWN).map(({ d, age }) => {
              const hue = d.onHold ? 'amber' : 'slate';
              return (
                <window.PhoneRow key={d.id} onClick={() => goToDeal(d.id)}
                  leading={(
                    <span style={{ width: 32, height: 32, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: HUE[hue].bg, color: HUE[hue].fg }}>
                      <Icon.Clock size={16} />
                    </span>
                  )}
                  title={pdDealLabel(d)}
                  subtitle={`${d.onHold ? `On hold · quiet ${Math.round(age)} days` : `No activity in ${Math.round(age)} days`} · ${pdDealStage(d)}`} />
              );
            })}
            {attn.data && !attn.error && !allAttn && attnList.length > PD_ATTENTION_SHOWN && (
              <PdMore label={`Show ${attnList.length - PD_ATTENTION_SHOWN} more`} onClick={() => setAllAttn(true)} />
            )}
          </PdGroup>
        </>
      )}

      <CK.EventSheet ev={openEv} onClose={() => setOpenEv(null)} activeUser={activeUser}
        goToCompany={goToCompany} goToContact={goToContact} onChanged={() => loadMeetings(true)} />
      <K.Sheet task={sheetTask} onClose={() => setSheetTask(null)} toggle={toggle}
        activeUser={activeUser} nav={nav} onEdit={setEditing} />
      {editing && (
        <window.TaskDrawer activeUser={activeUser} members={teamMembers || []} task={editing}
          onClose={() => setEditing(null)} onCreated={K.tasksChanged} />
      )}
    </window.PhoneScroll>
  );
};

window.PhoneToday = PhoneToday;
